import { useHeyApi } from "@platform/api";
import { getBpApiMarketingPresentationInfoOptions } from "@platform/api/bp-api";
import { useRfpCarriers, useRfpClientDetails } from "@platform/hooks";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { MarketingPresentationInfoResponse } from "@platform/api/bp-api";
import type { RfpCarriersResponse } from "@platform/hooks";
import type { RfpFacts } from "@platform/rumi-ui";

export type RfpFactsState =
  | { kind: "loading" }
  | { kind: "loadFailed" }
  | { kind: "ready"; facts: RfpFacts };

const byName = (a: string, b: string): number => a.localeCompare(b);

/** Every carrier the RFP goes to market with, whatever its connection status. */
const carriersToMarket = ({
  connected,
  unconnected,
  unknown,
}: RfpCarriersResponse): string[] =>
  [
    ...new Set(
      [...(connected ?? []), ...(unconnected ?? []), ...(unknown ?? [])].map(
        (carrier) => carrier.name,
      ),
    ),
  ].sort(byName);

/** The carriers the broker named as writing each product today. */
const incumbentCarriers = ({
  coverageCarriers,
}: MarketingPresentationInfoResponse): string[] =>
  [
    ...new Set(
      (coverageCarriers ?? []).flatMap((coverage) =>
        coverage.currentCarrier ? [coverage.currentCarrier] : [],
      ),
    ),
  ].sort(byName);

/**
 * The RFP's incumbent carriers, from bp-api's marketing presentation info:
 * the one place the broker's named incumbent per product is served.
 */
const useIncumbentCarriers = (rfpId: string): UseQueryResult<string[]> => {
  const { bpApiClient } = useHeyApi();

  return useQuery({
    ...getBpApiMarketingPresentationInfoOptions({
      client: bpApiClient,
      path: { id: rfpId },
    }),
    select: incumbentCarriers,
  });
};

/**
 * The RFP details the Launchpad shows beside Rumi's Read, from the portal's
 * own records: a Run reports what Rumi found, not these.
 */
export const useRfpFacts = (rfpId: string): RfpFactsState => {
  const client = useRfpClientDetails({ rfpId });
  const carriers = useRfpCarriers({ rfpId });
  const incumbents = useIncumbentCarriers(rfpId);

  if (client.isError || carriers.isError || incumbents.isError) {
    return { kind: "loadFailed" };
  }

  if (!client.data || !carriers.data || !incumbents.data) {
    return { kind: "loading" };
  }

  return {
    kind: "ready",
    facts: {
      rfpId,
      situsState: client.data.situsStateName || client.data.situsState || null,
      eligibleEmployees: client.data.numberOfEmployees,
      incumbentCarriers: incumbents.data,
      carriersToMarket: carriersToMarket(carriers.data),
    },
  };
};
